import React, { useCallback, useEffect, useState } from 'react';
import { useAuth, apiClient } from '../auth/AuthContext';
import { useNotification } from '../shared/NotificationContext';

interface MqttStatus {
  configured: boolean;
  connected: boolean;
  reconnecting: boolean;
  host: string | null;
  port: number | null;
  clientId: string | null;
  username: string | null;
  retainEnabled: boolean;
  topicPrefix: string | null;
  lastError: string | null;
  lastConnectedAt: string | null;
  lastDisconnectedAt: string | null;
}

interface LogEntry {
  timestamp: string;
  message: string;
}

interface CronRequestLogEntry extends LogEntry {
  ok?: boolean;
  httpStatus?: number;
  publishedCount?: number;
  skippedCount?: number;
  errorCount?: number;
  durationMs?: number;
}

interface ObservabilityStatus {
  available: boolean;
  periodMinutes: number;
  reason?: string;
  lambda?: {
    logGroup: string | null;
    invocations: number;
    errors: number;
    errorRate: number;
    avgDurationMs: number | null;
    maxDurationMs: number | null;
    throttles: number;
  };
  api?: {
    name: string | null;
    stage: string;
    accessLogGroup: string | null;
    accessLoggingEnabled: boolean;
    requests: number;
    errors4xx: number;
    errors5xx: number;
    avgLatencyMs: number | null;
  };
  cron?: {
    functionName: string;
    logGroup: string | null;
    invocations: number;
    errors: number;
    errorRate: number;
  } | null;
  recentErrors?: LogEntry[];
  recentMqttEvents?: LogEntry[];
  recentCronRequests?: CronRequestLogEntry[];
}

interface SystemStatus {
  timestamp: string;
  buildingId: string;
  runtime: {
    functionName: string;
    region: string;
    nodeVersion: string;
    uptimeSeconds: number;
  };
  database: {
    requestsTable: string;
    usersTable: string;
    pricingTable: string;
    requestCount: number;
    userCount: number;
    pricingRuleCount: number;
  };
  mqtt: MqttStatus;
  observability: ObservabilityStatus;
}

const formatDateTime = (value: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

const formatUptime = (seconds: number) => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

const StatusBadge: React.FC<{ status: 'ok' | 'warn' | 'error' | 'neutral'; label: string }> = ({ status, label }) => {
  const colors = {
    ok: { bg: 'rgba(34, 197, 94, 0.15)', color: '#16a34a', border: 'rgba(34, 197, 94, 0.35)' },
    warn: { bg: 'rgba(234, 179, 8, 0.15)', color: '#ca8a04', border: 'rgba(234, 179, 8, 0.35)' },
    error: { bg: 'rgba(239, 68, 68, 0.15)', color: '#dc2626', border: 'rgba(239, 68, 68, 0.35)' },
    neutral: { bg: 'var(--surface-hover)', color: 'var(--muted)', border: 'var(--border)' },
  };
  const style = colors[status];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
      }}
    >
      {label}
    </span>
  );
};

const StatRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ color: 'var(--muted)', fontSize: 14 }}>{label}</span>
    <span style={{ fontSize: 14, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
  </div>
);

const LogList: React.FC<{ entries: LogEntry[]; emptyMessage: string }> = ({ entries, emptyMessage }) => {
  if (entries.length === 0) {
    return <p style={{ color: 'var(--muted)', fontSize: 14, margin: 0 }}>{emptyMessage}</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
      {entries.map((entry, index) => (
        <div
          key={`${entry.timestamp}-${index}`}
          style={{
            padding: '8px 10px',
            borderRadius: 8,
            background: 'var(--surface-hover)',
            border: '1px solid var(--border)',
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <div style={{ color: 'var(--muted)', marginBottom: 4 }}>{formatDateTime(entry.timestamp)}</div>
          <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {entry.message}
          </div>
        </div>
      ))}
    </div>
  );
};

const CronRequestLogList: React.FC<{ entries: CronRequestLogEntry[]; emptyMessage: string }> = ({
  entries,
  emptyMessage,
}) => {
  if (entries.length === 0) {
    return <p style={{ color: 'var(--muted)', fontSize: 14, margin: 0 }}>{emptyMessage}</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
      {entries.map((entry, index) => {
        const status = entry.ok === false || (entry.httpStatus != null && entry.httpStatus >= 400)
          ? 'error'
          : (entry.errorCount ?? 0) > 0
            ? 'warn'
            : 'ok';
        const badge = {
          ok: { label: 'OK', color: '#16a34a' },
          warn: { label: 'Partial', color: '#ca8a04' },
          error: { label: 'Failed', color: '#dc2626' },
        }[status];

        return (
          <div
            key={`${entry.timestamp}-${index}`}
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              background: 'var(--surface-hover)',
              border: '1px solid var(--border)',
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: 'var(--muted)' }}>{formatDateTime(entry.timestamp)}</span>
              <span style={{ color: badge.color, fontWeight: 600 }}>{badge.label}</span>
            </div>
            <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {entry.message}
            </div>
            {entry.httpStatus != null && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, color: 'var(--muted)' }}>
                <span>HTTP {entry.httpStatus}</span>
                {entry.publishedCount != null && <span>{entry.publishedCount} published</span>}
                {entry.skippedCount != null && <span>{entry.skippedCount} skipped</span>}
                {entry.errorCount != null && entry.errorCount > 0 && (
                  <span style={{ color: '#dc2626' }}>{entry.errorCount} MQTT errors</span>
                )}
                {entry.durationMs != null && <span>{entry.durationMs} ms</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

function isRecentTimestamp(timestamp: string, maxAgeMinutes: number): boolean {
  const ts = Date.parse(timestamp);
  return Number.isFinite(ts) && Date.now() - ts <= maxAgeMinutes * 60 * 1000;
}

/** Cron ticks call the API every ~2m; recent success is a better live-health signal than 60m CloudWatch totals. */
function recentCronTicksHealthy(
  requests: CronRequestLogEntry[] | undefined,
  sampleSize = 3,
  maxAgeMinutes = 10,
): boolean {
  if (!requests?.length) return false;
  const recent = requests.filter((r) => isRecentTimestamp(r.timestamp, maxAgeMinutes));
  if (!recent.length) return false;
  return recent.slice(0, sampleSize).every((r) => r.ok);
}

const SystemPage: React.FC = () => {
  const { token } = useAuth();
  const { notify } = useNotification();
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState('');

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    try {
      setError('');
      const data = await apiClient.get<SystemStatus>('/admin/system/status');
      setStatus(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load system status');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
    const interval = window.setInterval(fetchStatus, 30000);
    return () => window.clearInterval(interval);
  }, [fetchStatus]);

  const handleReconnect = async () => {
    if (!token) return;
    try {
      setReconnecting(true);
      const result = await apiClient.post<{ ok: boolean; message: string; mqtt: MqttStatus }>(
        '/admin/mqtt/reconnect',
        {}
      );
      notify(result.message, result.ok ? 'success' : 'error');
      setStatus((prev) => (prev ? { ...prev, mqtt: result.mqtt } : prev));
      window.setTimeout(fetchStatus, 1500);
    } catch (err: any) {
      notify(err.message || 'Reconnect failed', 'error');
    } finally {
      setReconnecting(false);
    }
  };

  const mqtt = status?.mqtt;
  const obs = status?.observability;
  const cronRecentlyHealthy = recentCronTicksHealthy(obs?.recentCronRequests);
  const mqttBadge = !mqtt?.configured
    ? { status: 'neutral' as const, label: 'Not configured' }
    : mqtt.connected
      ? { status: 'ok' as const, label: 'Connected' }
      : mqtt.reconnecting
        ? { status: 'warn' as const, label: 'Reconnecting' }
        : { status: 'error' as const, label: 'Disconnected' };

  const apiBadge = !obs?.available
    ? { status: 'neutral' as const, label: 'Unavailable' }
    : cronRecentlyHealthy
      ? { status: 'ok' as const, label: 'Healthy' }
      : (obs.api?.errors5xx ?? 0) > 0
      ? { status: 'error' as const, label: '5xx errors' }
      : (obs.api?.errors4xx ?? 0) > 0
        ? { status: 'warn' as const, label: '4xx errors' }
        : { status: 'ok' as const, label: 'Healthy' };

  const lambdaBadge = !obs?.available
    ? { status: 'neutral' as const, label: 'Unavailable' }
    : cronRecentlyHealthy
      ? { status: 'ok' as const, label: 'Healthy' }
      : (obs.lambda?.errors ?? 0) > 0
      ? { status: 'warn' as const, label: 'Errors detected' }
      : { status: 'ok' as const, label: 'Healthy' };

  const cronBadge = !obs?.cron
    ? { status: 'neutral' as const, label: 'Not deployed' }
    : cronRecentlyHealthy
      ? { status: 'ok' as const, label: 'Running' }
      : (obs.cron.errors ?? 0) > 0
      ? { status: 'error' as const, label: 'Errors detected' }
      : (obs.cron.invocations ?? 0) > 0
        ? { status: 'ok' as const, label: 'Running' }
        : { status: 'warn' as const, label: 'No invocations' };

  return (
    <div className="ah-shell">
      <div className="ah-panel">
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div className="ah-card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <h1 className="ah-title">System</h1>
                <p style={{ margin: '8px 0 0', color: 'var(--muted)', fontSize: 14 }}>
                  Infrastructure status, CloudWatch metrics, and service controls
                </p>
              </div>
              <button type="button" className="ah-btn ah-btn-outline" onClick={fetchStatus} disabled={loading}>
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="ah-card" style={{ marginBottom: 16, borderColor: 'var(--danger)', color: 'var(--danger)' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            <div className="ah-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>MQTT Broker</h2>
                {mqtt && <StatusBadge {...mqttBadge} />}
              </div>

              {loading && !status ? (
                <p style={{ color: 'var(--muted)' }}>Loading...</p>
              ) : mqtt ? (
                <>
                  <StatRow label="Broker" value={mqtt.configured ? `${mqtt.host}:${mqtt.port}` : 'Not configured'} />
                  <StatRow label="Client ID" value={mqtt.clientId || '—'} />
                  <StatRow label="Username" value={mqtt.username || '—'} />
                  <StatRow label="Topic prefix" value={mqtt.topicPrefix || '—'} />
                  <StatRow label="Retained publishes" value={mqtt.retainEnabled ? 'Enabled' : 'Disabled'} />
                  <StatRow label="Last connected" value={formatDateTime(mqtt.lastConnectedAt)} />
                  <StatRow label="Last disconnected" value={formatDateTime(mqtt.lastDisconnectedAt)} />
                  {mqtt.lastError && (
                    <StatRow label="Last error" value={<span style={{ color: 'var(--danger)' }}>{mqtt.lastError}</span>} />
                  )}

                  <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="ah-btn"
                      onClick={handleReconnect}
                      disabled={!mqtt.configured || reconnecting || mqtt.connected}
                    >
                      {reconnecting ? 'Reconnecting...' : 'Attempt reconnect'}
                    </button>
                  </div>
                </>
              ) : null}
            </div>

            <div className="ah-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>API Health</h2>
                {obs && <StatusBadge {...apiBadge} />}
              </div>
              {obs?.available && obs.api ? (
                <>
                  <StatRow label="API" value={`${obs.api.name} (${obs.api.stage})`} />
                  <StatRow label={`Requests (last ${obs.periodMinutes}m)`} value={obs.api.requests} />
                  <StatRow label="4xx errors" value={obs.api.errors4xx} />
                  <StatRow label="5xx errors" value={obs.api.errors5xx} />
                  <StatRow label="Avg latency" value={obs.api.avgLatencyMs != null ? `${obs.api.avgLatencyMs} ms` : '—'} />
                  <StatRow label="Access logging" value={obs.api.accessLoggingEnabled ? 'Enabled' : 'Disabled'} />
                  {obs.api.accessLogGroup && (
                    <StatRow label="Access log group" value={obs.api.accessLogGroup} />
                  )}
                </>
              ) : (
                <p style={{ color: 'var(--muted)', fontSize: 14, margin: 0 }}>
                  {obs?.reason ?? 'Observability data unavailable'}
                </p>
              )}
            </div>

            <div className="ah-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>Lambda Metrics</h2>
                {obs && <StatusBadge {...lambdaBadge} />}
              </div>
              {obs?.available && obs.lambda ? (
                <>
                  <StatRow label={`Invocations (last ${obs.periodMinutes}m)`} value={obs.lambda.invocations} />
                  <StatRow label="Errors" value={obs.lambda.errors} />
                  <StatRow label="Error rate" value={`${obs.lambda.errorRate}%`} />
                  <StatRow label="Avg duration" value={obs.lambda.avgDurationMs != null ? `${obs.lambda.avgDurationMs} ms` : '—'} />
                  <StatRow label="Max duration" value={obs.lambda.maxDurationMs != null ? `${obs.lambda.maxDurationMs} ms` : '—'} />
                  <StatRow label="Throttles" value={obs.lambda.throttles} />
                  {obs.lambda.logGroup && <StatRow label="Log group" value={obs.lambda.logGroup} />}
                </>
              ) : (
                <p style={{ color: 'var(--muted)', fontSize: 14, margin: 0 }}>
                  {obs?.reason ?? 'Observability data unavailable'}
                </p>
              )}
            </div>

            <div className="ah-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>MQTT Cron Scheduler</h2>
                {obs && <StatusBadge {...cronBadge} />}
              </div>
              {obs?.cron ? (
                <>
                  <StatRow label="Function" value={obs.cron.functionName} />
                  <StatRow label={`Invocations (last ${obs.periodMinutes}m)`} value={obs.cron.invocations} />
                  <StatRow label="Errors" value={obs.cron.errors} />
                  <StatRow label="Error rate" value={`${obs.cron.errorRate}%`} />
                  {obs.cron.logGroup && <StatRow label="Log group" value={obs.cron.logGroup} />}
                </>
              ) : (
                <p style={{ color: 'var(--muted)', fontSize: 14, margin: 0 }}>
                  {obs?.available
                    ? 'Cron scheduler not configured for this deployment'
                    : (obs?.reason ?? 'Observability data unavailable')}
                </p>
              )}
            </div>

            <div className="ah-card">
              <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Runtime</h2>
              {status ? (
                <>
                  <StatRow label="Building ID" value={status.buildingId} />
                  <StatRow label="Lambda function" value={status.runtime.functionName} />
                  <StatRow label="Region" value={status.runtime.region} />
                  <StatRow label="Node.js" value={status.runtime.nodeVersion} />
                  <StatRow label="Container uptime" value={formatUptime(status.runtime.uptimeSeconds)} />
                  <StatRow label="Status as of" value={formatDateTime(status.timestamp)} />
                </>
              ) : (
                <p style={{ color: 'var(--muted)' }}>Loading...</p>
              )}
            </div>

            <div className="ah-card">
              <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Database</h2>
              {status ? (
                <>
                  <StatRow label="Access requests" value={status.database.requestCount} />
                  <StatRow label="Users" value={status.database.userCount} />
                  <StatRow label="Pricing rules" value={status.database.pricingRuleCount} />
                  <StatRow label="Requests table" value={status.database.requestsTable} />
                  <StatRow label="Users table" value={status.database.usersTable} />
                  <StatRow label="Pricing table" value={status.database.pricingTable} />
                </>
              ) : (
                <p style={{ color: 'var(--muted)' }}>Loading...</p>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', marginTop: 16 }}>
            <div className="ah-card">
              <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Recent Errors</h2>
              <p style={{ margin: '0 0 12px', color: 'var(--muted)', fontSize: 13 }}>
                From CloudWatch Logs{obs?.periodMinutes ? ` (last ${obs.periodMinutes} minutes)` : ''}
              </p>
              <LogList
                entries={obs?.recentErrors ?? []}
                emptyMessage="No ERROR log entries in the selected period"
              />
            </div>

            <div className="ah-card">
              <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Recent MQTT Events</h2>
              <p style={{ margin: '0 0 12px', color: 'var(--muted)', fontSize: 13 }}>
                From CloudWatch Logs{obs?.periodMinutes ? ` (last ${obs.periodMinutes} minutes)` : ''}
              </p>
              <LogList
                entries={obs?.recentMqttEvents ?? []}
                emptyMessage="No MQTT log entries in the selected period"
              />
            </div>
          </div>

          <div className="ah-card" style={{ marginTop: 16 }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Recent Cron Requests</h2>
            <p style={{ margin: '0 0 12px', color: 'var(--muted)', fontSize: 13 }}>
              Scheduled calls to <code style={{ fontSize: 12 }}>POST /mqtt/process</code>
              {obs?.periodMinutes ? ` (last ${obs.periodMinutes} minutes)` : ''}
            </p>
            <CronRequestLogList
              entries={obs?.recentCronRequests ?? []}
              emptyMessage={
                obs?.cron
                  ? 'No cron scheduler log entries in the selected period'
                  : 'Cron scheduler logs are only available when the cron lambda is deployed'
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemPage;
